import { Component } from '@angular/core';

import { AuthorizedUsersDialogComponent } from '@components/authorized-users-dialog/authorized-users-dialog.component';

import { NominationCreationDto } from '@models/votes/nomination-creation.dto';
import { NominationProposalDto } from '@models/votes/nomination-proposal.dto';
import { NominationStatus } from '@models/votes/nomination-type.enum';

import { AccountService } from '@services/account.service';
import { MessageService } from '@services/message.service';
import { NominationService } from '@services/nomination.service';
import { SubscriberService } from '@services/subscriber.service';

import {
  DialogService,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';

import { catchError, throwError } from 'rxjs';

@Component({
  selector: 'ebs-nomination',
  templateUrl: './nomination.component.html',
  styleUrls: ['./nomination.component.scss'],
})
export class NominationComponent {

  public nominations: NominationProposalDto[] = [];

  public editingNominations: {
    [key: number]: boolean;
  } = {};

  public creating: NominationProposalDto | undefined;

  public userIds: number[] = [];

  public NominationStatus: typeof NominationStatus = NominationStatus;

  public nominationDictionary = {
    [NominationStatus.ACCEPTED]: 'Accettata',
    [NominationStatus.REJECTED]: 'Rifiutata',
    [NominationStatus.PENDING]: 'In attesa',
    [NominationStatus.CANCELLED]: 'Cancellata',
    [NominationStatus.REQUESTED_ANOTHER]: 'Già presentata',
  };

  private editingBackups: {
    [key: number]: NominationProposalDto;
  } = {};

  constructor(
    private nominationService: NominationService,
    private messageService: MessageService,
    private subscriberService: SubscriberService,
    private accountService: AccountService,
    private dialogService: DialogService,
  ) {
    this.loadNominations();
  }

  /* CARICAMENTO CANDIDATURE */

  loadNominations(): void {
    this.nominationService
      .getNominationProposalList()
      .subscribe((nominations) => {

        this.nominations = nominations.map((elem) => {
          elem.expanded = false;

          elem.startDate = new Date(elem.startDate);
          elem.endDate = new Date(elem.endDate);

          return elem;
        });

      });
  }

  /* CREAZIONE */

  createNewNomination(): void {
    this.userIds = [];

    this.creating = {
      description: '',
      endDate: new Date(),
      startDate: new Date(),
      eventName: '',
      title: '',
    };
  }

  cancelCreation(): void {
    this.creating = undefined;
    this.userIds = [];
  }

  /* SELEZIONE ISCRITTI */

  assignUsers(): void {
    this.accountService.getAuthList().subscribe((allUsers) => {

      const dialogRef: DynamicDialogRef = this.dialogService.open(
        AuthorizedUsersDialogComponent,
        {
          maximizable: false,
          draggable: false,
          height: '100%',
          width: '100%',
          closable: true,
          closeOnEscape: false,
          data: {
            selectedUsers: [],
            allUsers,
            allowSimplifiedView: true,
          },
        },
      );

      dialogRef.onClose.subscribe((authorized: any[]) => {
        if (!authorized) {
          return;
        }

        this.userIds = authorized.map(
          (elem) => elem.subscriber.subscriberId,
        );
      });

    });
  }

  /* ELIMINAZIONE */

  removeNomination(nomination: NominationProposalDto): void {
    this.nominationService
      .removeNomination([nomination])
      .pipe(
        catchError((err) => {
          this.messageService.error(
            'Rimozione candidatura',
            'Errore durante la rimozione',
          );

          return throwError(() => err);
        }),
      )
      .subscribe(() => {
        this.loadNominations();

        this.messageService.success(
          'Rimozione candidatura',
          'Rimozione avvenuta con successo',
        );
      });
  }

  /* SALVATAGGIO NUOVA CANDIDATURA */

  createNomination(): void {
    if (!this.creating) {
      return;
    }

    const nomination: NominationCreationDto = {
      proposal: this.creating,
      userIds: this.userIds,
    };

    this.nominationService
      .createNomination(nomination)
      .pipe(
        catchError((err) => {
          this.messageService.error(
            'Creazione candidatura',
            'Errore durante la creazione',
          );

          return throwError(() => err);
        }),
      )
      .subscribe(() => {
        this.loadNominations();

        this.creating = undefined;
        this.userIds = [];

        this.messageService.success(
          'Creazione candidatura',
          'Creazione avvenuta con successo',
        );
      });
  }

  /* AVVIO MODIFICA */

  startEditing(nomination: NominationProposalDto): void {
    const proposalId = nomination.proposalId;

    if (proposalId === undefined || proposalId === null) {
      return;
    }

    this.editingBackups[proposalId] = {
      ...nomination,
      startDate: new Date(nomination.startDate),
      endDate: new Date(nomination.endDate),
    };

    this.editingNominations[proposalId] = true;
  }

  /* ANNULLAMENTO MODIFICA */

  cancelEditing(nomination: NominationProposalDto): void {
    const proposalId = nomination.proposalId;

    if (proposalId === undefined || proposalId === null) {
      return;
    }

    const backup = this.editingBackups[proposalId];

    if (backup) {
      Object.assign(nomination, backup);
    }

    delete this.editingBackups[proposalId];
    this.editingNominations[proposalId] = false;
  }

  /* SALVATAGGIO MODIFICA */

  modifyNomination(nomination: NominationProposalDto): void {
    this.nominationService
      .modifyNomination([nomination])
      .pipe(
        catchError((err) => {
          this.messageService.error(
            'Modifica candidatura',
            'Errore durante la modifica',
          );

          return throwError(() => err);
        }),
      )
      .subscribe(() => {

        if (
          nomination.proposalId !== undefined &&
          nomination.proposalId !== null
        ) {
          delete this.editingBackups[nomination.proposalId];
          this.editingNominations[nomination.proposalId] = false;
        }

        this.loadNominations();

        this.messageService.success(
          'Modifica candidatura',
          'Modifica avvenuta con successo',
        );
      });
  }

  /* ACCETTAZIONE */

  acceptNomination(
    proposalId: number,
    userId: number,
  ): void {
    this.nominationService
      .acceptNomination(proposalId, userId)
      .subscribe(() => {
        this.loadNominations();
      });
  }

  /* RIFIUTO */

  rejectNomination(
    proposalId: number,
    userId: number,
  ): void {
    this.nominationService
      .rejectNomination(proposalId, userId)
      .subscribe(() => {
        this.loadNominations();
      });
  }

  /* CANCELLAZIONE */

  cancelNomination(
    proposalId: number,
    userId: number,
  ): void {
    this.nominationService
      .cancelNomination(proposalId, userId)
      .subscribe(() => {
        this.loadNominations();
      });
  }
}